import type { Expression } from '../../lib/characters.js';
import type { LiveMood } from '../../lib/voice/controller.js';
import type { BackdropPalette } from './LiveBackdrop.js';
import { riveOn } from '../../lib/riveFlag.js';

/** Expressão do personagem vivo (Início e escolha do personagem) para cada personalidade. */
export const CARD_MOOD: Record<Expression, LiveMood> = {
  // Olhos sorrindo o tempo todo parecem olhos fechados: o sorriso fica na boca.
  simpatico: 'neutral',
  direto: 'neutral',
  divertido: 'laughing',
  elegante: 'curious',
  tranquilo: 'neutral',
  espontaneo: 'surprised',
};

/**
 * Humor do personagem parado (Início, escolha do jeito). No Igor cada humor vira um desenho
 * inteiro de expressão: parado ele fica sereno (pisca, olha, respira). Surpresa e riso ficam
 * para quando acontecem (um toque, a conversa).
 */
export function cardMood(characterKey: string, expression: Expression): LiveMood {
  const mood = CARD_MOOD[expression];
  if (!isQuadros(characterKey)) return mood;
  if (mood === 'surprised') return 'neutral';
  if (mood === 'laughing') return 'happy';
  return mood;
}

/** Faces de massinha: rosto inteiro na tela, olhos e boca trocados por camadas. */
export const FACE_KEYS = [
  'face-sol',
  'face-brasa',
  'face-pessego',
  'face-piscina',
  'face-lavanda',
] as const;

/** Personagens 2.5D por camadas (imagens de estúdio recortadas e animadas). */
/** Pessoa a traço: ela e ele, em vetor (Rive), sem camadas de imagem. */
export const TRACO_KEYS = ['traco-ela', 'traco-ele', 'traco-igor', 'traco-ela2'] as const;
/** A traço por quadros: boca e olhos trocados por desenhos inteiros (mapas do gerador). */
export const QUADROS_KEYS = ['traco-igor', 'traco-ela2'] as const;
export function isQuadros(key: string): boolean {
  return (QUADROS_KEYS as readonly string[]).includes(key);
}

export const LAYERED_KEYS = ['robo-s', 'gota', ...FACE_KEYS, ...TRACO_KEYS] as const;
export function isLayeredCharacter(key: string): boolean {
  return (LAYERED_KEYS as readonly string[]).includes(key);
}

/**
 * Personagens já montados no Rive (`/characters/<chave>/character.riv`). Cada um
 * só entra aqui depois de medido e aprovado contra as camadas.
 */
const RIVE_KEYS: readonly string[] = [...FACE_KEYS, 'gota', ...TRACO_KEYS];
/** Só existe em Rive: se o motor falhar, fica a imagem parada (não há camadas). */
export function riveOnly(key: string): boolean {
  return key === 'gota' || (TRACO_KEYS as readonly string[]).includes(key);
}
/** Só com a chave dos personagens em Rive ligada (ver `lib/riveFlag.ts`). */
export function usesRive(key: string): boolean {
  return RIVE_KEYS.includes(key) && riveOn();
}

/** Como o personagem ocupa o palco: rosto inteiro, busto que sai da borda ou corpo em pé. */
export function stageFraming(key: string): 'face' | 'bust' | 'portrait' | 'body' {
  if ((FACE_KEYS as readonly string[]).includes(key)) return 'face';
  if ((TRACO_KEYS as readonly string[]).includes(key)) return 'portrait';
  return key === 'gota' ? 'bust' : 'body';
}

/** Fundo harmônico com a cor de cada personagem. */
const PALETTES: Record<'robo-s' | 'gota' | 'traco' | 'padrao', BackdropPalette> = {
  // A traço: o papel do desenho; a luz e a sombra são do mesmo creme.
  traco: { light: '#FFFCF8', mid: '#FCF7F1', deep: '#EDE4D8', glow: '#FFFFFF' },
  // Robô: branco pérola com laranja da marca; fundo pêssego quente.
  'robo-s': { light: '#FFEADB', mid: '#FFC7A3', deep: '#EE8556', glow: '#FFF3EA' },
  // Gota: lavanda; fundo lilás com um toque de rosa.
  gota: { light: '#F1EAFF', mid: '#D4C4FF', deep: '#9A82EE', glow: '#F8F4FF' },
  padrao: { light: '#FFF1E6', mid: '#FFD3B5', deep: '#EE9B72', glow: '#FFF6EF' },
};

/** Cor do rosto de cada Face: o fundo vivo usa tons dela. */
const FACE_SKIN: Record<(typeof FACE_KEYS)[number], string> = {
  'face-sol': '#F8BC17',
  'face-brasa': '#FD7E3B',
  'face-pessego': '#FD9373',
  'face-piscina': '#4BC3D8',
  'face-lavanda': '#A3AAEA',
};

/**
 * Chão de massinha de cada Face: a cor média da borda da placa (o fundo da tela).
 * A luz em volta do rosto vem de `/characters/<face>/ground.webp`. As duas coisas
 * são geradas por `design/rive/gen_ground.py` (cores em `design/rive/ground.json`).
 */
const FACE_GROUND: Record<(typeof FACE_KEYS)[number], string> = {
  'face-sol': '#F9BE17',
  'face-brasa': '#FD7F3C',
  'face-pessego': '#FD9474',
  'face-piscina': '#4BC4D9',
  'face-lavanda': '#A3ABEA',
};

function mix(hex: string, target: string, amount: number): string {
  const a = Number.parseInt(hex.slice(1), 16);
  const b = Number.parseInt(target.slice(1), 16);
  const channel = (shift: number) =>
    Math.round(((a >> shift) & 255) * (1 - amount) + ((b >> shift) & 255) * amount);
  return `#${((channel(16) << 16) | (channel(8) << 8) | channel(0)).toString(16).padStart(6, '0')}`;
}

export function paletteFor(key: string): BackdropPalette {
  if (key in FACE_SKIN) {
    const skin = FACE_SKIN[key as keyof typeof FACE_SKIN];
    // O rosto é a tela: fundo quase chapado, com respiro de luz e sombra da própria cor.
    return {
      light: mix(skin, '#ffffff', 0.12),
      mid: skin,
      deep: mix(skin, '#000000', 0.12),
      glow: mix(skin, '#ffffff', 0.22),
    };
  }
  if (key === 'robo-s' || key === 'gota') return PALETTES[key];
  if ((TRACO_KEYS as readonly string[]).includes(key)) return PALETTES.traco;
  return PALETTES.padrao;
}

/** O chão de massinha da Face (cor, luz e o tom da sombra das bordas), ou nada para os demais. */
export function groundFor(key: string): { base: string; light: string; shade: string } | null {
  if (!(key in FACE_GROUND)) return null;
  const base = FACE_GROUND[key as keyof typeof FACE_GROUND];
  const deep = Number.parseInt(mix(base, '#000000', 0.5).slice(1), 16);
  return {
    base,
    light: `url('/characters/${key}/ground.webp')`,
    shade: `${(deep >> 16) & 255} ${(deep >> 8) & 255} ${deep & 255}`,
  };
}
